import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import {
  createAutomationServerWithOTP,
  deleteAutomationServer,
  getAutomationServers,
  getHetznerOptions,
  provisionHetznerServer,
} from "@/data/automation-server";

export const AUTOMATION_SERVERS_QUERY_KEY = ["automation-servers"];

export function useAutomationServersQuery(search?: string, orgId?: string) {
  const { isAuthenticated } = useAuth();

  return useInfiniteQuery({
    queryKey: [...AUTOMATION_SERVERS_QUERY_KEY, orgId ?? "active", search || ""],
    queryFn: ({ pageParam = 1 }) => getAutomationServers(pageParam, search, orgId),
    getNextPageParam: (lastPage) => {
      // If there's a next URL, extract the page number from it
      if (lastPage.next) {
        try {
          const url = new URL(lastPage.next);
          const page = url.searchParams.get("page");
          return page ? parseInt(page, 10) : undefined;
        } catch (error) {
          console.error("Error parsing next URL:", error);
          return undefined;
        }
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: isAuthenticated, // Only fetch when user is authenticated
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}

export function useCreateAutomationServer(orgId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, domain }: { name: string; domain?: string }) =>
      createAutomationServerWithOTP(name, domain, orgId),
    onSuccess: () => {
      // Invalidate automation servers when automation server is created successfully
      queryClient.invalidateQueries({ queryKey: AUTOMATION_SERVERS_QUERY_KEY });
    },
  });
}

export function useDeleteAutomationServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (serverId: string) => deleteAutomationServer(serverId),
    onSuccess: () => {
      // Invalidate automation servers when automation server is deleted successfully
      queryClient.invalidateQueries({ queryKey: AUTOMATION_SERVERS_QUERY_KEY });
    },
  });
}

export function useHetznerOptions(orgId?: string) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['hetzner-options', orgId ?? 'active'],
    queryFn: () => getHetznerOptions(orgId),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useProvisionHetznerServer(orgId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      subdomain: string;
      server_config_id: string;
      region: string;
    }) => provisionHetznerServer(data, orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTOMATION_SERVERS_QUERY_KEY });
    },
  });
}
